import React, { useEffect, useState } from 'react'
import { failureText, lmsAdmin } from '../api'
import { CopyLine } from '../copy-line'
import { LmsIcon } from '../lms-icon'
import { ROTATION_GRACE_DAYS } from './lti-tool-key-section'

// An LTI 1.3 connection's registration (Phase L Task 8; rulings L-2, L-3,
// L-20; Addenda 1 and 2: A1/B1 Deep Linking, B6 the email default). EdXR is
// the tool; the admin's LMS is the platform. Two sections on the connection's
// page, in the order the admin needs them:
//
//   1. "Tool details for your LMS": the tool's four addresses (login
//      initiation, launch, Deep Linking and keyset) and the settings to choose
//      in the LMS. They are there from the day the connection is made, as the
//      LMS asks for them first.
//   2. "Platform details from your LMS": what the LMS gives back (issuer,
//      client ID, deployment IDs and its authentication, token and keyset
//      addresses). The LMS service writes the six together (PUT), so this has
//      its own Save, and the connection's Save refuses to drop what is typed
//      here and not yet saved. The service's words on each field are shown
//      under it.
//
// The registration is read from its own route (GET .../lti). The issuer is
// compared with the LMS's `iss` exactly, so it is sent as typed, never
// trimmed or changed; everything else is sent without surrounding spaces.

export const MAX_DEPLOYMENTS = 20
export const MAX_DEPLOYMENT_LENGTH = 255

const FIELDS = ['issuer', 'client_id', 'auth_login_url', 'token_url', 'jwks_url']

const EMPTY = { issuer: '', client_id: '', auth_login_url: '', token_url: '', jwks_url: '', deployment_ids: [] }

const valuesOf = registration => {
  const values = { deployment_ids: [...((registration && registration.deployment_ids) || [])] }
  FIELDS.forEach(field => {
    values[field] = (registration && registration[field]) || ''
  })
  return values
}

const sameValues = (a, b) =>
  FIELDS.every(field => a[field] === b[field]) &&
  a.deployment_ids.length === b.deployment_ids.length &&
  a.deployment_ids.every((id, i) => id === b.deployment_ids[i])

// What each field is called in the page, to name it in the service's words.
const LABEL = {
  issuer: 'Issuer',
  client_id: 'Client ID',
  deployment_ids: 'Deployment IDs',
  auth_login_url: 'Authentication URL',
  token_url: 'Token URL',
  jwks_url: 'LMS keyset URL'
}

// What a required field says when it is empty.
const REQUIRED = {
  issuer: 'Enter the issuer (platform ID) your LMS shows.',
  client_id: 'Enter the client ID your LMS shows.',
  auth_login_url: 'Enter the authentication URL your LMS shows.',
  token_url: 'Enter the token URL your LMS shows.',
  jwks_url: "Enter your LMS's keyset URL."
}

// The service's refusals that need more than its own sentence.
const REFUSAL_WORDS = {
  LTI_ISSUER_HAS_LINKS:
    "The issuer can't change while learners are connected under it. Revoke their links on the Account Linking page first, then change it.",
  LTI_REGISTRATION_DUPLICATE: 'Another connection in your organization is already registered with this issuer and client ID.',
  CONNECTION_ARCHIVED: 'This connection is archived, so its registration can no longer change.'
}

const NO_ERRORS = { fields: {}, entries: {}, message: null }

// The service's words on a field are a lower-case fragment ("must not contain a
// space"), so each is shown after the field's name.
const named = (label, words) => (words ? `${label}: ${words}` : undefined)

// The service's 400 words: each field's own, and each deployment's by its
// index in the list sent (keyed here by the entry).
const refusedWith = (error, sent) => {
  const said = (error && error.details && error.details.fields) || {}
  const fields = {}
  Object.keys(LABEL).forEach(field => {
    fields[field] = named(LABEL[field], said[field])
  })
  const entries = {}
  sent.forEach((id, i) => {
    if (said[`deployment_ids[${i}]`]) entries[id] = named('Deployment ID', said[`deployment_ids[${i}]`])
  })
  return { fields, entries, message: REFUSAL_WORDS[error && error.code] || failureText(error, "The details couldn't be saved.") }
}

// A control character (the service refuses one in a deployment ID).
const hasControl = text => Array.from(text).some(ch => ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127)

// The registration as the page edits it: read once, then the platform's
// fields as typed (values), the deployment box (draft), and the service's
// words on a save it refused.
export const useLtiRegistration = (connectionId, enabled) => {
  const [registration, updateRegistration] = useState(null)
  const [loadError, updateLoadError] = useState(null)
  const [values, updateValues] = useState(EMPTY)
  const [draft, updateDraft] = useState('')
  const [draftError, updateDraftError] = useState(null)
  const [errors, updateErrors] = useState(NO_ERRORS)
  const [saving, updateSaving] = useState(false)
  const [saved, updateSaved] = useState(false)

  // A registration the service answers: the addresses and the key stay as they
  // were if an answer does not repeat them.
  const take = result => {
    updateRegistration(previous => ({
      ...result,
      tool_urls: result.tool_urls || (previous && previous.tool_urls),
      tool_key: result.tool_key || (previous && previous.tool_key)
    }))
    updateValues(valuesOf(result))
  }

  useEffect(() => {
    if (!enabled || !connectionId) return undefined
    let live = true
    updateRegistration(null)
    updateLoadError(null)
    lmsAdmin
      .getLtiRegistration(connectionId)
      .then(result => {
        if (!live) return
        take(result)
      })
      .catch(e => {
        if (live) updateLoadError(e)
      })
    return () => {
      live = false
    }
  }, [connectionId, enabled])

  // What the registration holds: nothing of the platform's until it is written.
  const baseline = registration && registration.registered ? valuesOf(registration) : EMPTY
  const dirty = registration !== null && (draft.trim() !== '' || !sameValues(values, baseline))

  const edited = () => updateSaved(false)

  const set = (field, value) => {
    updateValues(previous => ({ ...previous, [field]: value }))
    updateErrors(previous => ({ ...previous, fields: { ...previous.fields, [field]: undefined } }))
    edited()
  }

  const setDraft = value => {
    updateDraft(value)
    updateDraftError(null)
  }

  const add = () => {
    const entry = draft.trim()
    let problem = null
    if (values.deployment_ids.length >= MAX_DEPLOYMENTS) problem = `At most ${MAX_DEPLOYMENTS} deployment IDs.`
    else if (!entry) problem = 'Enter a deployment ID.'
    else if (entry.length > MAX_DEPLOYMENT_LENGTH) problem = `At most ${MAX_DEPLOYMENT_LENGTH} characters.`
    else if (hasControl(entry)) problem = 'A deployment ID has no control characters.'
    else if (values.deployment_ids.includes(entry)) problem = 'Already in the list.'
    updateDraftError(problem)
    if (problem) return
    updateValues(previous => ({ ...previous, deployment_ids: [...previous.deployment_ids, entry] }))
    updateErrors(previous => ({ ...previous, fields: { ...previous.fields, deployment_ids: undefined }, entries: {} }))
    updateDraft('')
    edited()
  }

  const remove = id => {
    updateValues(previous => ({ ...previous, deployment_ids: previous.deployment_ids.filter(entry => entry !== id) }))
    updateErrors(previous => ({ ...previous, entries: {} }))
    edited()
  }

  // The checks the service would make first, so an empty field is named
  // without a round trip. The service is the authority on the rest.
  const validate = sent => {
    const found = {}
    FIELDS.forEach(field => {
      // (the issuer is sent as typed, but a blank one is no issuer)
      if (!sent[field].trim()) found[field] = REQUIRED[field]
    })
    if (sent.deployment_ids.length === 0) found.deployment_ids = 'Add at least one deployment ID.'
    if (draft.trim()) updateDraftError('Press Add to add this deployment ID, or clear it.')
    updateErrors({ fields: found, entries: {}, message: null })
    return Object.keys(found).length === 0 && !draft.trim()
  }

  const save = async () => {
    if (saving) return false
    const sent = {
      // the LMS's own `iss`, compared exactly: never trimmed or changed
      issuer: values.issuer,
      client_id: values.client_id.trim(),
      deployment_ids: values.deployment_ids.map(id => id.trim()),
      auth_login_url: values.auth_login_url.trim(),
      token_url: values.token_url.trim(),
      jwks_url: values.jwks_url.trim()
    }
    updateSaved(false)
    if (!validate(sent)) return false
    updateSaving(true)
    try {
      take(await lmsAdmin.putLtiRegistration(connectionId, sent))
      updateErrors(NO_ERRORS)
      updateSaved(true)
      return true
    } catch (e) {
      updateErrors(refusedWith(e, sent.deployment_ids))
      return false
    } finally {
      updateSaving(false)
    }
  }

  // Back to what the registration holds.
  const discard = () => {
    updateValues(baseline)
    updateDraft('')
    updateDraftError(null)
    updateErrors(NO_ERRORS)
    updateSaved(false)
  }

  const setToolKey = toolKey => updateRegistration(previous => (previous ? { ...previous, tool_key: toolKey } : previous))

  return {
    registration, loadError, values, draft, draftError, errors, saving, saved, dirty,
    set, setDraft, add, remove, save, discard, setToolKey
  }
}

const ToolSetting = ({ id, children }) => (
  <li data-testid={id} style={{ padding: '2px 0' }}>
    {children}
  </li>
)

// One numbered step of the set-up guide: its title in plain words, then what to
// do and the settings to choose.
const SetupStep = ({ id, title, children }) => (
  <li data-testid={id} style={{ padding: '4px 0' }}>
    <strong>{title}</strong>
    {children}
  </li>
)

// "Setting up EdXR in Moodle" (Phase L Task 11; Addenda 1 and 2: A1/B1 Deep
// Linking, B6 the email default, B7 the cached key set). The steps in the order
// an admin does them, with Moodle's names for the options as its "Configure a
// tool manually" form words them (Task 0's facts, 4.5):
//   1. add the tool from the four addresses above;
//   2. set the content selection URL to the Deep Linking address, or the LMS's
//      "Select content" cannot reach EdXR (B1);
//   3. accept grades, and enrol learners with a gradebook role (the service
//      refuses to send a grade for a learner who has none);
//   4. share the email only if the LMS confirms email changes (B6; vouching
//      trusts the LMS's email);
//   5. copy the LMS's own details back into the section below;
//   6. after revoking the tool key, purge the LMS's caches (B7).
const LmsSettings = () => (
  <div className="field" data-testid="lti-lms-settings">
    <div className="field-label">
      <span className="name">Setting up EdXR in Moodle</span>
    </div>
    <div className="field-help" style={{ marginTop: 0 }}>
      Do these steps in order. They use Moodle's names for the options. Another LMS may word them differently.
    </div>
    <ol className="lti-settings" data-testid="lti-setup-steps">
      <SetupStep id="lti-step-add-tool" title="Add EducationXR as an LTI 1.3 external tool.">
        {' '}
        In Moodle, open Site administration › Plugins › Activity modules › External tool › Manage tools and choose "Configure a
        tool manually". Copy the addresses above into it and choose:
        <ul className="lti-settings">
          <ToolSetting id="lti-setting-version">
            LTI version: <strong>LTI 1.3</strong>
          </ToolSetting>
          <ToolSetting id="lti-setting-urls">
            Tool URL and Redirection URI(s): the launch URL. Initiate login URL: the login initiation URL.
          </ToolSetting>
          <ToolSetting id="lti-setting-keytype">
            Public key type: <strong>Keyset URL</strong>, with Public keyset set to the keyset URL.
          </ToolSetting>
          <ToolSetting id="lti-setting-container">
            Default launch container: <strong>New window</strong>
          </ToolSetting>
        </ul>
      </SetupStep>
      <SetupStep id="lti-step-content-selection" title="Set the content selection URL.">
        <ul className="lti-settings">
          <ToolSetting id="lti-setting-deep-linking">
            Supports Deep Linking (Content-Item Message): <strong>on</strong>. Content Selection URL: the Deep Linking URL above,
            otherwise choosing a course from your LMS won't work.
          </ToolSetting>
        </ul>
      </SetupStep>
      <SetupStep id="lti-step-grades" title="Let EducationXR send grades back.">
        <ul className="lti-settings">
          <ToolSetting id="lti-setting-grades">
            Accept grades from the tool: <strong>Always</strong>. With Delegate to teacher, the teacher must tick it in each
            activity, or no grades go back.
          </ToolSetting>
          <ToolSetting id="lti-setting-ags">
            IMS LTI Assignment and Grade Services: <strong>Use this service for grade sync and column management</strong>. "Use
            this service for grade sync only" also sends grades, but only to a grade item your LMS has already made for the
            activity: EducationXR can't make one.
          </ToolSetting>
          <ToolSetting id="lti-setting-gradebook-role">
            Learners need a gradebook role. In Moodle, enrol them as <strong>Student</strong>. A learner with no gradebook role
            gets no grade, and the failure shows under "Failed grade returns" on this page.
          </ToolSetting>
        </ul>
      </SetupStep>
      <SetupStep id="lti-step-email" title="Decide whether learners are matched by email.">
        <ul className="lti-settings">
          <ToolSetting id="lti-setting-email">
            Share launcher's email with tool: <strong>Always</strong>. This is needed for automatic linking of learners;
            Moodle's default is Never. Without it, each learner signs in to EducationXR once.
            <div className="field-help" style={{ marginTop: 2 }} data-testid="lti-email-risk">
              Turn this on only if your LMS confirms email changes (Moodle: search Site administration for "Email change
              confirmation", the setting <code>emailchangeconfirmation</code>, and turn it on). Otherwise a learner could change
              their email to someone else's and be linked to that person's account.
            </div>
            <div className="field-help" style={{ marginTop: 2 }} data-testid="lti-email-needs">
              Automatic linking also needs "Require a sign-in for every launch" to be off and "Who can connect an account" to be
              set to anyone with an EducationXR account. It is never used for a person whose email domain signs in through your
              organization's single sign-on.
            </div>
          </ToolSetting>
        </ul>
      </SetupStep>
      <SetupStep id="lti-step-paste-back" title="Copy your LMS's details back here.">
        {' '}
        Save the tool in Moodle, then open its configuration details. Copy the platform ID (the issuer), the client ID, the
        deployment ID and the authentication, token and keyset addresses into "Platform details from your LMS" below, and save.
        Until you do, no launch is accepted.
      </SetupStep>
      <SetupStep id="lti-step-revoke" title="If you revoke the tool key, purge Moodle's caches.">
        {' '}
        Moodle keeps the old key set until its caches are purged (Site administration › Development › Purge caches). Rotating the
        key instead keeps the old key valid for {ROTATION_GRACE_DAYS} days, so nothing needs purging.
      </SetupStep>
    </ol>
    <div className="field-help" data-testid="lti-custom-parameter">
      Each course activity needs the custom parameter <code>edxr_course=&lt;course ID&gt;</code>. Choose "Select content" in
      your LMS and EducationXR fills it in for you (Deep Linking). To add it by hand, copy the line from the course's LMS
      integration page.
    </div>
  </div>
)

const ToolDetails = ({ registration, readOnly }) => {
  const urls = registration && registration.tool_urls
  return (
    <div className="form-section" data-testid="lti-tool-details">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="plug" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Tool details for your LMS</h2>
      </div>
      <div className="field-help" style={{ marginTop: 0, marginBottom: 10 }}>
        Add EducationXR to your LMS as an LTI 1.3 external tool with these addresses. They are ready now: your LMS asks for them
        first, and gives you its own details afterwards (below).
      </div>
      {urls ? (
        <React.Fragment>
          <CopyLine testId="lti-url-login" label="Login initiation URL" value={urls.login_initiation_url} />
          <CopyLine testId="lti-url-launch" label="Launch URL (redirect URI)" value={urls.launch_url} />
          <CopyLine testId="lti-url-deep-link" label="Deep Linking URL (content selection)" value={urls.deep_linking_url} />
          <CopyLine testId="lti-url-jwks" label="Keyset URL" value={urls.jwks_url} />
          <LmsSettings />
        </React.Fragment>
      ) : (
        <div className="field-help" data-testid="lti-no-addresses">
          {readOnly
            ? "The tool's addresses aren't available: an archived connection's registration is removed."
            : "This connection's tool addresses aren't made yet. They appear once the platform details below are saved for the first time. If your LMS needs the addresses before it gives you those details, make a new connection: it shows its addresses at once."}
        </div>
      )}
    </div>
  )
}

const FieldError = ({ id, message }) =>
  message ? (
    <div className="field-error" data-testid={id}>
      <LmsIcon name="circle-exclamation" />
      {message}
    </div>
  ) : null

const TextField = ({ id, slug, label, help, lti, field, readOnly, placeholder }) => (
  <div className="field">
    <div className="field-label">
      <label className="name" htmlFor={id}>
        {label}
      </label>
    </div>
    <input
      id={id}
      type="text"
      className={`form-control form-control-sm ${lti.errors.fields[field] ? 'is-invalid' : ''}`}
      placeholder={placeholder}
      value={lti.values[field]}
      disabled={readOnly}
      autoComplete="off"
      spellCheck={false}
      onChange={e => lti.set(field, e.target.value)}
      data-testid={`lti-${slug}`}
    />
    <FieldError id={`lti-${slug}-error`} message={lti.errors.fields[field]} />
    {help && <div className="field-help">{help}</div>}
  </div>
)

const PlatformDetails = ({ lti, readOnly, unsavedError }) => {
  const { registration, values, errors } = lti
  const isRegistered = !!(registration && registration.registered)
  // (the read-only page is the archived connection's)
  const archived = readOnly
  const full = values.deployment_ids.length >= MAX_DEPLOYMENTS
  return (
    <div className="form-section" data-testid="lti-platform-details">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="link" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Platform details from your LMS</h2>
        <span className="spacer" />
        <span className={`pill ${archived ? 'neutral' : isRegistered ? 'success' : 'warning'}`} data-testid="lti-status">
          <span className="dot" />
          {archived ? 'Archived' : isRegistered ? 'Registered' : "Waiting for your LMS's details"}
        </span>
      </div>
      <div className="field-help" style={{ marginTop: 0, marginBottom: 10 }}>
        {archived
          ? 'This connection is archived: it accepts no launch, and these details can no longer change.'
          : 'Once EducationXR is added in your LMS, it shows these. Copy them here. Until then no launch is accepted.'}
      </div>

      {errors.message && (
        <div className="error-banner" data-testid="lti-platform-error">
          <LmsIcon name="circle-exclamation" />
          <div>
            <p className="title">{errors.message}</p>
          </div>
        </div>
      )}

      <TextField
        id="lti-issuer"
        slug="issuer"
        label="Issuer (platform ID)"
        help="Exactly as your LMS shows it, including any slash at the end. It is compared letter for letter, so it is never changed."
        lti={lti}
        field="issuer"
        readOnly={readOnly}
        placeholder="https://lms.acme.com"
      />
      <TextField id="lti-client-id" slug="client-id" label="Client ID" lti={lti} field="client_id" readOnly={readOnly} />

      <div className="field" data-testid="lti-deployments">
        <div className="field-label">
          <label className="name" htmlFor="lti-deployment-input">
            Deployment IDs
          </label>
          <span className="optional" data-testid="lti-deployments-count">
            {values.deployment_ids.length} of {MAX_DEPLOYMENTS}
          </span>
        </div>
        {values.deployment_ids.length > 0 && (
          <ul className="list-unstyled mb-0">
            {values.deployment_ids.map(id => (
              <li key={id} data-testid={`lti-deployment-${id}`} style={{ padding: '3px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="lti-mono" style={{ flex: 1, minWidth: 0 }}>
                    {id}
                  </span>
                  {!readOnly && (
                    <button
                      className="btn btn-light border btn-sm"
                      type="button"
                      aria-label={`Remove deployment ${id}`}
                      data-testid={`lti-deployment-remove-${id}`}
                      onClick={() => lti.remove(id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <FieldError id={`lti-deployment-${id}-error`} message={errors.entries[id]} />
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            id="lti-deployment-input"
            type="text"
            className={`form-control form-control-sm ${lti.draftError ? 'is-invalid' : ''}`}
            placeholder="A deployment ID from your LMS"
            value={lti.draft}
            disabled={full || readOnly}
            autoComplete="off"
            spellCheck={false}
            onChange={e => lti.setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                lti.add()
              }
            }}
            data-testid="lti-deployment-input"
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            type="button"
            disabled={full || readOnly}
            data-testid="lti-deployment-add"
            onClick={lti.add}
          >
            Add
          </button>
        </div>
        <FieldError id="lti-deployment-error" message={lti.draftError} />
        <FieldError id="lti-deployments-error" message={errors.fields.deployment_ids} />
        <div className="field-help" data-testid="lti-deployments-help">
          {full
            ? `At most ${MAX_DEPLOYMENTS} deployment IDs. Remove one to add another.`
            : `One for each place EducationXR is added in your LMS; a launch from any other is refused. At most ${MAX_DEPLOYMENTS}, each at most ${MAX_DEPLOYMENT_LENGTH} characters.`}
        </div>
      </div>

      <TextField
        id="lti-auth-login-url"
        slug="auth-login-url"
        label="Authentication URL"
        help="Where EducationXR sends the learner to confirm the launch with your LMS."
        lti={lti}
        field="auth_login_url"
        readOnly={readOnly}
        placeholder="https://lms.acme.com/mod/lti/auth.php"
      />
      <TextField
        id="lti-token-url"
        slug="token-url"
        label="Token URL"
        help="Where EducationXR asks your LMS for permission to send grades."
        lti={lti}
        field="token_url"
        readOnly={readOnly}
        placeholder="https://lms.acme.com/mod/lti/token.php"
      />
      <TextField
        id="lti-jwks-url"
        slug="jwks-url"
        label="LMS keyset URL"
        help="Where EducationXR finds the keys that prove a launch came from your LMS."
        lti={lti}
        field="jwks_url"
        readOnly={readOnly}
        placeholder="https://lms.acme.com/mod/lti/certs.php"
      />

      {!readOnly && (
        <div className="field">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={!lti.dirty || lti.saving}
              data-testid="lti-platform-save"
              onClick={lti.save}
            >
              {lti.saving ? 'Saving…' : 'Save platform details'}
            </button>
            {lti.dirty && (
              <button
                className="btn btn-light border btn-sm"
                type="button"
                disabled={lti.saving}
                data-testid="lti-platform-discard"
                onClick={lti.discard}
              >
                Discard changes
              </button>
            )}
            {lti.saved && (
              <span className="field-help" style={{ marginTop: 0 }} data-testid="lti-platform-saved">
                <LmsIcon name="check" className="me-1" />
                Platform details saved.
              </span>
            )}
          </div>
          {lti.dirty && unsavedError && <FieldError id="lti-unsaved-error" message={unsavedError} />}
        </div>
      )}
    </div>
  )
}

// Both sections, or what stands in for them while the registration is read or
// when it could not be. `readOnly`: the connection is archived.
export const LtiRegistrationSection = ({ lti, readOnly, unsavedError }) => {
  // The connection's page is open to the LMS read and write roles, but its
  // registration is read from a route that needs the LMS admin scope: a 403 is
  // a person without it, not a failure to report.
  if (lti.loadError && lti.loadError.status === 403) {
    return (
      <div className="form-section" data-testid="lti-registration-forbidden">
        <div className="form-section-title">
          <span className="num">
            <LmsIcon name="plug" style={{ fontSize: 9 }} />
          </span>
          <h2 className="label">LTI 1.3 registration</h2>
        </div>
        <div className="field-help">Only an LMS administrator can see this connection's LTI registration.</div>
      </div>
    )
  }
  if (lti.loadError) {
    return (
      <div className="form-section" data-testid="lti-registration-error">
        <div className="form-section-title">
          <span className="num">
            <LmsIcon name="plug" style={{ fontSize: 9 }} />
          </span>
          <h2 className="label">LTI 1.3 registration</h2>
        </div>
        <div className="field-error">
          <LmsIcon name="circle-exclamation" />
          {failureText(lti.loadError, "The registration couldn't be loaded.")}
        </div>
      </div>
    )
  }
  if (!lti.registration) {
    return (
      <div className="form-section" data-testid="lti-registration-loading">
        <div className="form-section-title">
          <span className="num">
            <LmsIcon name="plug" style={{ fontSize: 9 }} />
          </span>
          <h2 className="label">LTI 1.3 registration</h2>
        </div>
        <div className="field-help">Loading…</div>
      </div>
    )
  }
  return (
    <React.Fragment>
      <ToolDetails registration={lti.registration} readOnly={readOnly} />
      <PlatformDetails lti={lti} readOnly={readOnly} unsavedError={unsavedError} />
    </React.Fragment>
  )
}
